import { PermissionCode, type TenantOperator } from '@gb-ticket/shared-types';
import { Loader2Icon, MoreHorizontalIcon, PlusIcon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminPanelClassName, adminTableFrameClassName } from '@/platform/features/admin-panel';
import {
  TenantFormDialog,
  toTenantFormValues,
  type TenantFormValues,
} from '@/platform/features/tenants/tenant-form-dialog';
import {
  useClearTenantLogo,
  useCreateTenant,
  useTenants,
  useUpdateTenant,
  useUploadTenantLogo,
} from '@/platform/features/tenants/use-tenants';
import { useDialogInvalidationHold } from '@/platform/lib/use-invalidation-hold';
import { ApiRequestError } from '@/lib/api';
import { cn } from '@/lib/utils';

type DialogState = { mode: 'create' } | { mode: 'edit'; values: TenantFormValues } | null;

export function TenantsAdminPage() {
  const me = useMe();
  const canManage = hasPermission(me.data, PermissionCode.TENANTS_MANAGE);
  const tenants = useTenants();
  const createTenant = useCreateTenant();
  const updateTenant = useUpdateTenant();
  const uploadLogo = useUploadTenantLogo();
  const clearLogo = useClearTenantLogo();
  const [dialog, setDialog] = useState<DialogState>(null);
  const [search, setSearch] = useState('');
  useDialogInvalidationHold(dialog !== null);

  const busy =
    createTenant.isPending || updateTenant.isPending || uploadLogo.isPending || clearLogo.isPending;

  const list = tenants.data?.items ?? [];
  const q = search.trim().toLowerCase();
  const items = q
    ? list.filter((row) => row.name.toLowerCase().includes(q) || row.slug.toLowerCase().includes(q))
    : list;

  async function handleFormSubmit(payload: {
    create?: Parameters<typeof createTenant.mutateAsync>[0];
    update?: Parameters<typeof updateTenant.mutateAsync>[0]['body'];
    logoFile?: File | null;
    clearLogo?: boolean;
  }) {
    try {
      if (dialog?.mode === 'create' && payload.create) {
        const created = await createTenant.mutateAsync(payload.create);
        if (payload.logoFile) {
          await uploadLogo.mutateAsync({ id: created.id, file: payload.logoFile });
        }
        toast.success('Mandant angelegt.');
        setDialog(null);
        return;
      }
      if (dialog?.mode === 'edit' && payload.update && dialog.values.id) {
        await updateTenant.mutateAsync({ id: dialog.values.id, body: payload.update });
        if (payload.clearLogo) {
          await clearLogo.mutateAsync(dialog.values.id);
        } else if (payload.logoFile) {
          await uploadLogo.mutateAsync({ id: dialog.values.id, file: payload.logoFile });
        }
        toast.success('Mandant gespeichert.');
        setDialog(null);
      }
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Mandant konnte nicht gespeichert werden.',
      );
    }
  }

  async function setStatus(item: TenantOperator, status: 'active' | 'suspended') {
    try {
      await updateTenant.mutateAsync({ id: item.id, body: { status } });
      toast.success(status === 'suspended' ? 'Mandant gesperrt.' : 'Mandant entsperrt.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Status konnte nicht geändert werden.',
      );
    }
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Mandanten"
        description="Firmen dieser Installation. Nur der Heimat-Mandant sieht diese Seite."
        action={
          canManage ? (
            <Button type="button" onClick={() => setDialog({ mode: 'create' })}>
              <PlusIcon className="size-4" aria-hidden />
              Mandant anlegen
            </Button>
          ) : undefined
        }
      />

      <div className={`${adminPanelClassName} max-w-sm`}>
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Name oder Slug suchen …"
          aria-label="Mandanten suchen"
        />
      </div>

      {tenants.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Mandanten werden geladen …
        </p>
      )}

      {tenants.isError && (
        <p role="alert" className="text-sm text-destructive">
          {tenants.error instanceof ApiRequestError
            ? tenants.error.message
            : 'Die Mandanten konnten nicht geladen werden.'}
        </p>
      )}

      {tenants.data && (
        <div className={adminTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Status</TableHead>
                {canManage && (
                  <TableHead className="w-12">
                    <span className="sr-only">Aktionen</span>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={canManage ? 4 : 3}
                    className="text-center text-muted-foreground"
                  >
                    Keine Mandanten gefunden.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  <TableRow
                    key={item.id}
                    className={cn(item.status === 'suspended' && 'opacity-70')}
                  >
                    <TableCell className="font-medium">
                      {item.name}
                      {item.isInstallationHome ? (
                        <span className="ml-2 text-xs text-muted-foreground">Heimat</span>
                      ) : null}
                    </TableCell>
                    <TableCell className="font-mono text-sm">{item.slug}</TableCell>
                    <TableCell>
                      {item.status === 'active' ? (
                        <Badge variant="outline">Aktiv</Badge>
                      ) : (
                        <Badge variant="destructive">Gesperrt</Badge>
                      )}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon-sm"
                              aria-label={`${item.name}: Aktionen`}
                            >
                              <MoreHorizontalIcon className="size-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem
                              onSelect={() =>
                                setDialog({ mode: 'edit', values: toTenantFormValues(item) })
                              }
                            >
                              Bearbeiten
                            </DropdownMenuItem>
                            {!item.isInstallationHome &&
                              (item.status === 'active' ? (
                                <DropdownMenuItem
                                  variant="destructive"
                                  onSelect={() => void setStatus(item, 'suspended')}
                                >
                                  Sperren
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem onSelect={() => void setStatus(item, 'active')}>
                                  Entsperren
                                </DropdownMenuItem>
                              ))}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <TenantFormDialog
        open={dialog !== null}
        mode={dialog?.mode ?? 'create'}
        initial={dialog?.mode === 'edit' ? dialog.values : null}
        busy={busy}
        onOpenChange={(open) => {
          if (!open) {
            setDialog(null);
          }
        }}
        onSubmit={handleFormSubmit}
      />
    </section>
  );
}
